import React, { useState, useEffect, useCallback } from 'react';
import styles from './ValueLevelVariables.module.css';
import PageHeader from '../pageheader/Pageheader.tsx';
import { getglobalVariables, getDataSets, saveStudyVariables, getsavedStudyVariables, generateDefine, defineHistory } from '../../api/api.js';
import Dropdown from '../dropdown/Dropdown.tsx';
import { useToast } from '../toast/ToastProvider.tsx';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import PageLoader from '../pageloader/PageLoader.tsx';
import Modal from '../modal/Modal.tsx';

interface GlobalVariable {
  id: string;
  name: string;
  selected: boolean;
  whereClauseVariables: string;
  topicVar: string;
}

interface StudySpecificVariable {
  id: string;
  dataset: string;
  targetVariable: string;
  whereClauseVariable: string[];
}

interface DropdownOption {
  value: string;
  label: string;
}

interface Dataset {
  datasetName: string;
  variables: string[];
  studyFileId: number;
}

const ValueLevelVariables: React.FC = () => {
  const showToast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { studyId, defineVersion } = location.state || {};

  // State
  const [loading, setLoading] = useState<boolean>(false);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [selectedDataset, setSelectedDataset] = useState<string>('');
  const [availableVariables, setAvailableVariables] = useState<GlobalVariable[]>([]);
  const [selectedGlobalVariables, setSelectedGlobalVariables] = useState<GlobalVariable[]>([]);
  const [studyVariables, setStudyVariables] = useState<StudySpecificVariable[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  
  // Track initial state for change detection
  const [initialGlobalVariables, setInitialGlobalVariables] = useState<GlobalVariable[]>([]);
  const [initialStudyVariables, setInitialStudyVariables] = useState<StudySpecificVariable[]>([]);
  
  // State for unsaved changes confirmation modal
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  
  // Check if there are any changes
  const hasChanges = () => {
    // Compare global variables
    const globalChanged = JSON.stringify(selectedGlobalVariables) !== JSON.stringify(initialGlobalVariables);
    
    // Compare study variables
    const studyChanged = JSON.stringify(studyVariables) !== JSON.stringify(initialStudyVariables);
    
    return globalChanged || studyChanged;
  };

  // Filter available variables to exclude already selected ones
  const availableVariablesFiltered = availableVariables.filter(variable => 
    !selectedGlobalVariables.some(selected => selected.name === variable.name)
  );
  // Dropdown options
  const [datasetOptions, setDatasetOptions] = useState<DropdownOption[]>([]);
  const [targetVariableOptions, setTargetVariableOptions] = useState<DropdownOption[]>([]);
  const [whereClauseOptions, setWhereClauseOptions] = useState<DropdownOption[]>([]);

  // Form state
  const [newVariable, setNewVariable] = useState({
    dataset: '',
    targetVariable: '',
    whereClauseVariable: [] as string[]
  });

  // Fetch global variables
  const fetchGlobalVariables = useCallback(async () => {
    try {
      const response = await getglobalVariables();
      const variables = response.data.map((item: any, index: number) => ({
        id: String(index),
        name: item.globalVariable,
        selected: false,
        whereClauseVariables: item.whereClauseVariables,
        topicVar: item.topicVar

      }));
      setAvailableVariables(variables);
    } catch (error) {
      console.error('Error fetching global variables:', error);
      throw error;
    }
  }, []);

  // Fetch datasets
  const fetchDatasets = useCallback(async () => {
    if (!studyId) return;
    try {
      const response = await getDataSets(studyId, defineVersion || '');
      setDatasets(response.data);
      setDatasetOptions(
        response.data.map((ds: Dataset) => ({
          value: ds.datasetName,
          label: ds.datasetName
        }))
      );
    } catch (error: any) {
      console.error('Error fetching datasets:', error);
      showToast.error('Failed to load datasets');
    }
  }, [studyId, defineVersion, showToast]);

  // Fetch saved study variables
  const fetchSavedVariables = useCallback(async () => {
    if (!studyId) return;
    try {
      const response = await getsavedStudyVariables(studyId, defineVersion || '1');
      const savedData = response.data;
      
      if (savedData && savedData.variables) {
        // Separate global and study-specific variables
        const globalVars: GlobalVariable[] = [];
        const studyVars: StudySpecificVariable[] = [];
        
        savedData.variables.forEach((variable: any, index: number) => {
          if (variable.datasetName === "") {
            // Global variable
            globalVars.push({
              id: variable.variableId,
              name: variable.targetVariable,
              selected: false, // Saved global variables are selected by default
              whereClauseVariables: variable.whereClauseVariables,
              topicVar: variable.topicVar
            });
          } else {
            // Study-specific variable
            studyVars.push({
              id: variable.variableId,
              dataset: variable.datasetName,
              targetVariable: variable.targetVariable,
              whereClauseVariable: variable.whereClauseVariables.split(',') // Convert comma-separated string to array
            });
          }
        });
        
        // Update state with fetched data
        setSelectedGlobalVariables(globalVars);
        setStudyVariables(studyVars);
        
        // Set initial state for change detection
        setInitialGlobalVariables([...globalVars]);
        setInitialStudyVariables([...studyVars]);
      }
    } catch (error) {
      console.error('Error fetching saved variables:', error);
      // Don't show error toast on initial load, as there might be no saved data
    }
  }, [studyId, defineVersion]);

  // Fetch all initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchGlobalVariables(),
          fetchDatasets(),
          fetchSavedVariables()
        ]);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch data on component mount
  // useEffect(() => {
  //   const fetchData = async () => {
  //     if (!studyId) {
  //       showToast.error('Study ID is missing');
  //       return;
  //     }

  //     setLoading(true);
  //     try {
  //       await Promise.all([fetchGlobalVariables(), fetchDatasets()]);
  //     } catch (error) {
  //       console.error('Error fetching data:', error);
  //       showToast.error('Failed to load data');
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchData();
  // }, []);

  // Toggle variable selection in available list
  const handleAvailableVariableToggle = (id: string) => {
    setAvailableVariables(prev =>
      prev.map(variable =>
        variable.id === id
          ? { ...variable, selected: !variable.selected }
          : variable
      )
    );
  };

  // Toggle variable selection in selected list
  const handleSelectedVariableToggle = (id: string) => {
    setSelectedGlobalVariables(prev =>
      prev.map(variable =>
        variable.id === id
          ? { ...variable, selected: !variable.selected }
          : variable
      )
    );
  };

  // Move variables between available and selected lists
  const moveVariables = (direction: 'toSelected' | 'toAvailable') => {
    if (direction === 'toSelected') {
      const varsToMove = availableVariables.filter(v => v.selected);
      setSelectedGlobalVariables(prev => [
        ...prev,
        ...varsToMove.map(v => ({ ...v, selected: false }))
      ]);
      setAvailableVariables(prev => prev.filter(v => !v.selected));
    } else {
      const varsToMove = selectedGlobalVariables.filter(v => v.selected);
      console.log("varsToMove", varsToMove);
      console.log("selectedGlobalVariables", selectedGlobalVariables);
   
      setAvailableVariables(prev => {
        // Filter out any existing variables with the same name to prevent duplicates
        const filteredPrev = prev.filter(existingVar => 
          !varsToMove.some(movedVar => movedVar.name === existingVar.name)
        );
        
        // Add the moved variables
        return [
          ...filteredPrev,
          ...varsToMove.map(v => ({ ...v, selected: false }))
        ];
      });
      console.log("availableVariables", availableVariables);
      setSelectedGlobalVariables(prev => prev.filter(v => !v.selected));
    }
  };


  // Get new global variables (selected ones that weren't in initial state)
  const getNewGlobalVariables = () => {
    return selectedGlobalVariables.filter(selected => 
      !initialGlobalVariables.some(initial => initial.name === selected.name)
    );
  };

  // Get new study variables (ones that weren't in initial state)
  const getNewStudyVariables = () => {
    return studyVariables.filter(studyVar => {
      const initialMatch = initialStudyVariables.find(initial => 
        initial.dataset === studyVar.dataset && 
        initial.targetVariable === studyVar.targetVariable
      );
      return !initialMatch;
    });
  };

  // Get updated study variables (ones that exist but have changed values)
  const getUpdatedStudyVariables = () => {
    return studyVariables.filter(studyVar => {
      const initialMatch = initialStudyVariables.find(initial => 
        initial.dataset === studyVar.dataset && 
        initial.targetVariable === studyVar.targetVariable
      );
      
      if (!initialMatch) return false; // Skip new variables
      
      // Check if whereClauseVariable has changed
      const initialWhereClause = initialMatch.whereClauseVariable.join(',');
      const currentWhereClause = studyVar.whereClauseVariable.join(',');
      
      return initialWhereClause !== currentWhereClause;
    });
  };

  // Get deleted global variables (those that were in initial state but are no longer selected)
  const getDeletedGlobalVariables = () => {
    console.log("Initial Global Variables:", initialGlobalVariables);
    console.log("Current Selected Global Variables:", selectedGlobalVariables);
    
    const deleted = initialGlobalVariables.filter(initial => 
      !selectedGlobalVariables.some(selected => selected.id === initial.id)
    );
    console.log("Deleted Global Variables:", deleted);
    return deleted;
  };

  // Get deleted study variables (those that were in initial state but are no longer present)
  const getDeletedStudyVariables = () => {
    console.log("Initial Study Variables:", initialStudyVariables);
    console.log("Current Study Variables:", studyVariables);
    
    const deleted = initialStudyVariables.filter(initial => 
      !studyVariables.some(current => current.id === initial.id)
    );
    console.log("Deleted Study Variables:", deleted);
    return deleted;
  };

  const handleSubmit = async () => {
    if (!hasChanges()) {
      showToast.info('No changes to save');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Prepare variables array for payload
      const variables: Array<{
        variableId?: number | "";
        datasetName: string;
        targetVariable: string;
        whereClauseVariables: string;
        topicVar: string;
      }> = [];
      
      // Add only NEW global variables
      const newGlobalVars = getNewGlobalVariables();
      newGlobalVars.forEach(globalVar => {
        variables.push({
          datasetName: "", // Empty for global variables
          targetVariable: globalVar.name,
          whereClauseVariables: globalVar.whereClauseVariables,
          topicVar: globalVar.topicVar
        });
      });
      
      // Add only NEW study-specific variables
      const newStudyVars = getNewStudyVariables();
      newStudyVars.forEach(studyVar => {
        variables.push({
          variableId: "", // Send empty string for new variables
          datasetName: studyVar.dataset,
          targetVariable: studyVar.targetVariable,
          whereClauseVariables: studyVar.whereClauseVariable.join(','), // Convert array to comma-separated string
          topicVar: "" // Empty for study-specific variables
        });
      });
      
      // Add ONLY UPDATED study-specific variables (skip new ones)
      const updatedStudyVars = getUpdatedStudyVariables();
      updatedStudyVars.forEach(studyVar => {
        const initialMatch = initialStudyVariables.find(initial => 
          initial.dataset === studyVar.dataset && 
          initial.targetVariable === studyVar.targetVariable
        );
        
        variables.push({
          variableId: initialMatch?.id ? parseInt(initialMatch.id) : "",
          datasetName: studyVar.dataset,
          targetVariable: studyVar.targetVariable,
          whereClauseVariables: studyVar.whereClauseVariable.join(','), // Convert array to comma-separated string
          topicVar: "" // Empty for study-specific variables
        });
      });
      
      // Get deleted variables
      const deletedGlobalVars = getDeletedGlobalVariables();
      const deletedStudyVars = getDeletedStudyVariables();
      console.log("deletedGlobalVars", deletedGlobalVars);
      console.log("deletedStudyVars", deletedStudyVars);
      
      // Prepare deletedVariableIds array
      const deletedVariableIds: number[] = [];
      
      // Add deleted global variable IDs
      deletedGlobalVars.forEach(globalVar => {
        if (globalVar.id && !isNaN(Number(globalVar.id))) {
          deletedVariableIds.push(Number(globalVar.id));
        }
      });
      
      // Add deleted study variable IDs
      deletedStudyVars.forEach(studyVar => {
        if (studyVar.id && !isNaN(Number(studyVar.id))) {
          deletedVariableIds.push(Number(studyVar.id));
        }
      });
      
      // Proceed if there are new variables OR deleted variables
      if (variables.length === 0 && deletedVariableIds.length === 0) {
        showToast.info('No changes to save');
        setIsSubmitting(false);
        return;
      }
      
      // Prepare the final payload
      const payload = {
        studyId: Number(studyId),
        defineVersion: defineVersion || '1',
        variables: variables,
        deletedVariableIds: deletedVariableIds
      };
      
      const response = await saveStudyVariables(payload);
      
      if (response.status === 200) {
        showToast.success('Variables saved successfully!');
        fetchSavedVariables();
        // Update initial state to current state after successful save
        setInitialGlobalVariables([...selectedGlobalVariables]);
        setInitialStudyVariables([...studyVariables]);
      } else {
        showToast.error('Failed to save variables');
      }
    } catch (error) {
      console.error('Error saving variables:', error);
      showToast.error('An error occurred while saving variables');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle dataset selection
  const handleDatasetChange = (value: string | string[]) => {
    const datasetValue = Array.isArray(value) ? value[0] : value;
    
    // Handle case when dataset is cleared
    if (!datasetValue) {
      setSelectedDataset('');
      setNewVariable(prev => ({
        ...prev,
        dataset: '',
        targetVariable: '',
        whereClauseVariable: []
      }));
      setTargetVariableOptions([]);
      setWhereClauseOptions([]);
      return;
    }
    
    const selected = datasets.find(ds => ds.datasetName === datasetValue);
    if (!selected) return;

    setSelectedDataset(datasetValue);
    setNewVariable(prev => ({
      ...prev,
      dataset: datasetValue,
      targetVariable: '',
      whereClauseVariable: []
    }));

    const options = selected.variables.map(varName => ({
      value: varName,
      label: varName
    }));
    setTargetVariableOptions(options);
    setWhereClauseOptions([]);
  };

  // Handle target variable selection
  const handleTargetVariableChange = (value: string | string[]) => {
    const targetValue = Array.isArray(value) ? value[0] : value;
    const selected = datasets.find(ds => ds.datasetName === selectedDataset);
    if (!selected) return;

    setNewVariable(prev => ({
      ...prev,
      targetVariable: targetValue,
      whereClauseVariable: []
    }));

    const options = selected.variables
      .filter(variable => variable !== targetValue)
      .map(variable => ({
        value: variable,
        label: variable
      }));
    setWhereClauseOptions(options);
  };

  // Handle where clause variable selection (multiselect)
  const handleWhereClauseChange = (value: string | string[]) => {
    const clauseValues = Array.isArray(value) ? value : [value];
    setNewVariable(prev => ({
      ...prev,
      whereClauseVariable: clauseValues
    }));
  };


  const handleGenerateDefine = async () => {
    // Check if there are unsaved changes
    if (hasChanges()) {
      setShowUnsavedChangesModal(true);
      return;
    }

    // If no unsaved changes, proceed with generate define
    proceedWithGenerateDefine();
  };

  const proceedWithGenerateDefine = async () => {
    const response = await generateDefine(studyId);
    if(response.status === 200){
      showToast.success(response.data.message);
      navigate(`/defineui/assigned-studies`, {replace: true});
    }
  };

  const handleConfirmGenerateDefine = () => {
    setShowUnsavedChangesModal(false);
    proceedWithGenerateDefine();
  };

  const handleCancelGenerateDefine = () => {
    setShowUnsavedChangesModal(false);
  };

  // Add new study variable
  const handleAddVariable = () => {
    if (!newVariable.dataset || !newVariable.targetVariable) {
      showToast.error('Please select a dataset and target variable');
      return;
    }

    // Check if the same dataset and target variable combination already exists
    const isDuplicate = studyVariables.some(existingVar => 
      existingVar.dataset === newVariable.dataset && 
      existingVar.targetVariable === newVariable.targetVariable
    );

    if (isDuplicate) {
      showToast.error('This dataset and target variable combination already exists');
      return;
    }

    const newVar: StudySpecificVariable = {
      id: Date.now().toString(),
      dataset: newVariable.dataset,
      targetVariable: newVariable.targetVariable,
      whereClauseVariable: newVariable.whereClauseVariable
    };

    setStudyVariables(prev => [newVar, ...prev]);

    // Reset form but keep the same dataset selected
    setNewVariable({
      dataset: '',
      targetVariable: '',
      whereClauseVariable: []
    });

    // Reset target and where clause options
    const selected = datasets.find(ds => ds.datasetName === selectedDataset);
    if (selected) {
      const options = selected.variables.map(varName => ({
        value: varName,
        label: varName
      }));
      setTargetVariableOptions(options);
      setWhereClauseOptions([]);
    }
  };

  const handleBack = ()=>{
       navigate(-1);
    }

  // Remove study variable
  const handleRemoveVariable = (id: string) => {
    setStudyVariables(prev => prev.filter(v => v.id !== id));
  };

  // Format where clause variables for display
  const formatWhereClause = (variables: string[]): string => {
    if (!variables || variables.length === 0) return '-';
    return variables.join(', ');
  };

  // Edit state
  const [editingVariable, setEditingVariable] = useState<string | null>(null);
  const [editWhereClause, setEditWhereClause] = useState<string[]>([]);

  // Handle edit button click
  const handleEditVariable = (variable: StudySpecificVariable) => {
    setEditingVariable(variable.id);

    // Populate the form with the variable's values
    setNewVariable({
      dataset: variable.dataset,
      targetVariable: variable.targetVariable,
      whereClauseVariable: variable.whereClauseVariable
    });

    // Update the dropdown options
    const selected = datasets.find(ds => ds.datasetName === variable.dataset);
    if (selected) {
      const targetOptions = selected.variables.map(varName => ({
        value: varName,
        label: varName
      }));
      setTargetVariableOptions(targetOptions);

      const whereOptions = selected.variables
        .filter(v => v !== variable.targetVariable)
        .map(v => ({ value: v, label: v }));
      setWhereClauseOptions(whereOptions);
    }
  };

  // Handle save edit
  const handleSaveEdit = () => {
    if (editingVariable) {
      setStudyVariables(prev =>
        prev.map(v =>
          v.id === editingVariable
            ? { ...v, whereClauseVariable: newVariable.whereClauseVariable }
            : v
        )
      );
      setEditingVariable(null);
      // Reset form
      setNewVariable({
        dataset: '',
        targetVariable: '',
        whereClauseVariable: []
      });
      setTargetVariableOptions([]);
      setWhereClauseOptions([]);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingVariable(null);
    // Reset form
    setNewVariable({
      dataset: '',
      targetVariable: '',
      whereClauseVariable: []
    });
    setTargetVariableOptions([]);
    setWhereClauseOptions([]);
  };

  // Show error if no study ID
  if (!studyId) {
    return (
      <div className={styles.errorContainer}>
        <p>No study selected. Please go back and select a study.</p>
      </div>
    );
  }

  const userPermissions = {
    canCreateStudy: true,
    canViewDefineFiles: true,
    canUploadDocuments: true,
    canViewDefinePreview: true,
    canAccessIssueManagement: true,
    canViewProgressCharts: true,
  };

  return (
    <>
      <PageHeader
        title="Value Level Variables"
        showRefresh={false}
        showCreateStudy={true}
        showDefineFiles={true}
        showUploadDocuments={true}
        showDefinePreview={true}
        showIssueManagement={true}
        showProgressCharts={true}
        selectedStudyId={0}
        permissions={userPermissions}
        onCreateStudy={() => console.log('Create new study')}
        onDefineFiles={() => console.log('View define files')}
        onUploadDocuments={() => console.log('Upload documents')}
        onDefinePreview={() => console.log('View define preview')}
        onIssueManagement={() => console.log('Issue management')}
        onProgressCharts={() => console.log('Progress charts')}
      />

      <div className={styles.container}>
        {loading ? (
          <PageLoader message="Loading Page..." />
        ) : (
          <>
            {/* Global Level VLM Variables Section */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Global level VLM variables</h2>

              <div className={styles.transferContainer}>
                {/* Available Variables */}
                <div className={styles.variableBox}>
                  <div className={styles.boxHeader}>
                    <h3>Available Variables</h3>
                  </div>
                  <div className={styles.variableList}>
                    {availableVariablesFiltered.map(variable => (
                      <div key={variable.id} className={styles.variableItem}>
                        <input
                          type="checkbox"
                          checked={variable.selected}
                          onChange={() => handleAvailableVariableToggle(variable.id)}
                          id={`available-${variable.id}`}
                        />
                        <label htmlFor={`available-${variable.id}`}>{variable.name}</label>
                      </div>
                    ))}
                    {availableVariablesFiltered.length === 0 && (
                      <div className={styles.emptyState}>
                        <p>No variables available</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Transfer Buttons */}
                <div className={styles.transferButtons}>
                  <button
                    className={styles.transferButton}
                    onClick={() => moveVariables('toSelected')}
                    disabled={!availableVariables.some(v => v.selected)}
                    aria-label="Move selected variables to selected list"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button
                    className={styles.transferButton}
                    onClick={() => moveVariables('toAvailable')}
                    disabled={!selectedGlobalVariables.some(v => v.selected)}
                    aria-label="Move selected variables to available list"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>

                {/* Selected Variables */}
                <div className={styles.variableBox}>
                  <div className={styles.boxHeader}>
                    <h3>Selected Variables</h3>
                  </div>
                  <div className={styles.variableList}>
                    {selectedGlobalVariables.map(variable => (
                      <div key={variable.id} className={styles.variableItem}>
                        <input
                          type="checkbox"
                          checked={variable.selected}
                          onChange={() => handleSelectedVariableToggle(variable.id)}
                          id={`selected-${variable.id}`}
                        />
                        <label htmlFor={`selected-${variable.id}`}>{variable.name}</label>
                      </div>
                    ))}
                    {selectedGlobalVariables.length === 0 && (
                      <div className={styles.emptyState}>
                        <p>No variables selected</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Study Specific VLM Variables Section */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Study specific VLM variables</h2>

              <div className={styles.studyVariablesContainer}>
                <div className={styles.formRow}>
                  <div className={styles.formGroupContainer}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Datasets</label>
                    <Dropdown
                      options={datasetOptions}
                      value={newVariable.dataset}
                      onChange={handleDatasetChange}
                      placeholder="Select dataset"
                      disabled={!!editingVariable}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Target Variables</label>
                    <Dropdown
                      options={targetVariableOptions}
                      value={newVariable.targetVariable}
                      onChange={handleTargetVariableChange}
                      placeholder="Select target variable"
                      disabled={!newVariable.dataset || !!editingVariable}

                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Where Clause Variables</label>
                    <Dropdown
                      options={whereClauseOptions}
                      value={newVariable.whereClauseVariable}
                      onChange={handleWhereClauseChange}
                      placeholder="Select where clause variables (optional)"
                      disabled={!newVariable.targetVariable}
                      multiSelect={true}
                    />
                  </div>
                  </div>

                  {editingVariable ? (
                    <div className={styles.editActions}>
                      <button
                        className={styles.saveButton}
                        onClick={handleSaveEdit}
                        aria-label="Save changes"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Save
                      </button>
                      <button
                        className={styles.cancelButton}
                        onClick={handleCancelEdit}
                        aria-label="Cancel edit"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className={styles.addButton}
                      onClick={handleAddVariable}
                      disabled={!newVariable.dataset || !newVariable.targetVariable}
                      aria-label="Add variable"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className={styles.variablesTableContainer}>
  {/* Added inner scrollable container */}
  <div className={styles.tableContainer}>
    <table className={styles.variablesTable}>
      <thead>
        <tr>
          <th>Datasets</th>
          <th>Target variable</th>
          <th>Where clause variables</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {studyVariables.map(variable => (
          <tr key={variable.id}>
            <td>{variable.dataset}</td>
            <td>{variable.targetVariable}</td>
            <td>{formatWhereClause(variable.whereClauseVariable)}</td>
            <td>
              <div className={styles.actionButtons}>
                <button
                  className={styles.editButton}
                  onClick={() => handleEditVariable(variable)}
                  aria-label={`Edit ${variable.targetVariable}`}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  className={styles.deleteButton}
                  onClick={() => handleRemoveVariable(variable.id)}
                  aria-label={`Delete ${variable.targetVariable}`}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  
  {/* Empty state moved outside tableContainer */}
  {studyVariables.length === 0 && (
    <div className={styles.emptyState}>
      <p>No variables added yet. Use the form above to add variables.</p>
    </div>
  )}
</div>
              </div>

              
            </section>


               <div className={styles.footer}>
                   <button
                    className={styles.submitButton}
                    onClick={handleSubmit}
                    disabled={isSubmitting || !hasChanges()}
                 >
                    {isSubmitting ? (
                        <div className={styles.buttonLoader}></div>
                    ) : (
                        'Save'
                    )}
                 </button>

                               <button className={styles.backButton} onClick={handleBack}>Back</button>

                                 <button
                               className={styles.submitButton}
                               onClick={handleGenerateDefine}
                              //  disabled={isSubmitting || !hasChanges()}
                            >
                               Generate Define
                             </button>
                  </div>
          </>
        )}
      </div>

      {/* Unsaved Changes Confirmation Modal */}
      {showUnsavedChangesModal && (
        <Modal isOpen={showUnsavedChangesModal} onClose={handleCancelGenerateDefine} title="Unsaved Changes">
          <div className={styles.deleteModalContent}>
            <p>There are unsaved changes in the current file. Do you really want to generate define?</p>
            
            <div className={styles.deleteModalActions}>
              <button className={styles.deleteModalButton} onClick={handleConfirmGenerateDefine}>
                Yes
              </button>
              <button className={styles.cancelModalButton} onClick={handleCancelGenerateDefine}>
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ValueLevelVariables;